import { useAppDispatch } from 'app/hooks';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxButton } from 'components/RevxButton';
import { JobItem } from 'domains/workspaceDomain';
import { useJobStatusCollection } from 'hooks/useJobStatusCollection';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import JobAdjustmentModal from '../../components/JobAdjustmentModal';
import { useJobCanCancel } from '../../hooks/useJobCardActions';
import { useJobCancelJob } from '../../hooks/useJobCancelJob';
import './CardCancelJobBtn.less';

type Props = {
  data: JobItem;
  onSuccess?: () => void;
};

/**
 * Cancel Job as a button on the card itself, next to the card's other job-level actions.
 *
 * The entry in the Actions menu stays where it is -- this is a second way in, not a move, because
 * cancelling is common enough that a menu you have to open first is in the way. Everything behind
 * the click is the menu's: same availability rules, same cancellation record, same modal.
 *
 * It sits last in the card's extra slot so it stacks under the job-adjustment button rather than
 * displacing it on a job that has an adjustment waiting. The archive button shares the slot too,
 * but only at COMPLETED, where cancelling is never offered -- the two never appear together.
 */
const CardCancelJobBtn = ({ data, onSuccess }: Props) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const canCancel = useJobCanCancel(data);
  const { cancelJob } = useJobCancelJob(data, () => setModalVisible(true));
  const { workspace } = useAppDispatch();
  const statusFilters = useJobStatusCollection();

  const handleSuccess = () => {
    workspace.reloadJobList();
    workspace.getJobCountByStatusFilter(statusFilters);
    onSuccess?.();
  };

  if (!canCancel) {
    return null;
  }

  return (
    <>
      <RevxFlexLayout justify="justify-center" className="card-cancel-job">
        <RevxButton
          brandGreen
          className="card-cancel-job-btn"
          onClick={() => cancelJob()}
        >
          {t('workspace.tabs.moreActions.cancelJob')}
        </RevxButton>
      </RevxFlexLayout>
      <JobAdjustmentModal
        visible={modalVisible}
        jobId={data.id}
        onCancel={() => setModalVisible(false)}
        onSuccess={handleSuccess}
      />
    </>
  );
};

export default CardCancelJobBtn;
